<template>
    <div class="flex flex-grow flex-col items-center justify-center p-4">
        <!-- Deliberately not styled like 404's large warning numeral: this is an offer,
             not a failure, so the status code is a muted footnote and the addon carries
             the page. -->
        <div class="bg-primary-700 ring-primary-400/20 w-full max-w-lg rounded p-4 ring-1 ring-inset sm:p-6">
            <paywall-body>
                <template #dismiss>
                    <v-button color="secondary" @click="goBack">
                        <t path="navigation.errors.go_back" />
                    </v-button>
                </template>
            </paywall-body>
        </div>

        <p class="text-primary-400 mt-4 text-xs uppercase tracking-wide">
            <t path="generic.paywall.status" />
        </p>
    </div>

    <v-footer />
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import PaywallBody from '~/components/paywall/PaywallBody.vue';
import { goBackOr } from '~/helpers/navigation';
import { entitlementFor } from '~/plugins/entitlement';
import { featuresStateLoaded } from '~/plugins/feature';
import { useUserStore } from '~/stores';
import { usePaywallStore } from '~/stores/paywall';

const route = useRoute();
const paywall = usePaywallStore();
const userStore = useUserStore();

// Normally the middleware has already populated the store on its way here. On a refresh
// or a shared link it has not, so rebuild from the query it wrote alongside the redirect.
//
// Only the feature key travels in the URL, and the verdict is re-derived from it rather
// than carried: that keeps this leak-free, because a non-admin re-deriving the same key
// gets `hidden` and sees a generic page instead of the addon being advertised to them.
onMounted(() => {
    if (paywall.target) return;

    const feature = route.query.feature?.toString();
    if (!feature) return;

    // The router mounts this view before the settings and user payloads land, and both
    // are needed: the verdict comes from the feature payload, and only an admin is ever
    // told a lock is purchasable. Reading them early yields `hidden`, which leaves the
    // page showing generic copy for a lock we can name. Waiting mirrors what
    // resolveEntitlementGate already does for the routes that redirect here.
    const loaded = computed(() => featuresStateLoaded(feature) && userStore.isInitialized);

    const open = (): void => {
        paywall.openFor(entitlementFor(feature), route.query.from?.toString());
    };

    if (loaded.value) {
        open();

        return;
    }

    const unwatch = watch(loaded, (isLoaded) => {
        if (!isLoaded) return;

        unwatch();
        open();
    });
});

const goBack = (): void => goBackOr();
</script>
